import { getScopedDomain } from '../lib/tenantScope';
// Hotel Lite — wake-up call scheduling.
// One-time or recurring per-day-of-week, with TTS or recorded prompt.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Trash2, X, BellRing, BedDouble, Clock } from 'lucide-react';
import { api } from '../lib/api';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function WakeupCalls() {
  const me = api.me();
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);

  const { data: list = [] } = useQuery({
    queryKey: ['wakeup-calls', getScopedDomain(me?.domain_uuid)],
    queryFn: () => { const d = getScopedDomain(me?.domain_uuid); return d ? api.wakeupCalls.list({ domain_uuid: d }) : Promise.resolve([]); },
    enabled: !!getScopedDomain(me?.domain_uuid),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.wakeupCalls.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wakeup-calls', getScopedDomain(me?.domain_uuid)] }),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api.wakeupCalls.cancel(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wakeup-calls', getScopedDomain(me?.domain_uuid)] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Wake-up Calls</h1>
        <div className="flex items-center gap-2">
          <Link to="/room-status" className="btn"><BedDouble size={14} className="inline mr-1" /> Room Status</Link>
          <button className="btn-primary" onClick={() => setShowNew(true)}><Plus size={16} className="inline mr-1" /> Schedule</button>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead><tr><th>Extension</th><th>Schedule</th><th>Type</th><th>Status</th><th>Last attempt</th><th></th></tr></thead>
          <tbody>
            {list.map((w: any) => (
              <tr key={w.wakeup_uuid} className="hover:bg-elevated">
                <td className="font-mono">{w.extension || w.extension_uuid?.slice(0, 8)}</td>
                <td className="text-sm">{describeSchedule(w.schedule)}</td>
                <td><span className="badge">{w.type}</span></td>
                <td>
                  {w.status === 'pending' && <span className="badge bg-primary/20 text-primary">pending</span>}
                  {w.status === 'sent' && <span className="badge bg-success/20 text-success">sent</span>}
                  {w.status === 'failed' && <span className="badge bg-danger/20 text-danger">failed</span>}
                  {w.status === 'cancelled' && <span className="badge bg-textSecondary/20">cancelled</span>}
                </td>
                <td className="text-xs text-textSecondary">{w.last_attempt_at ? new Date(w.last_attempt_at).toLocaleString() : '—'}</td>
                <td>
                  {w.status === 'pending' && (
                    <button className="btn-ghost p-1 mr-1" onClick={() => cancel.mutate(w.wakeup_uuid)}>Cancel</button>
                  )}
                  <button className="btn-ghost p-1 text-danger" onClick={() => confirm('Delete?') && remove.mutate(w.wakeup_uuid)}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-textSecondary">No wake-up calls scheduled.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showNew && getScopedDomain(me?.domain_uuid) && <NewWakeupModal domain_uuid={getScopedDomain(me?.domain_uuid)!} onClose={() => setShowNew(false)} />}
    </div>
  );
}

function describeSchedule(s: any) {
  if (!s) return '—';
  if (s.one_time) return `Once: ${new Date(s.one_time).toLocaleString()}`;
  if (s.recurring) return `${(s.recurring.days || []).join(',')} @ ${s.recurring.time}`;
  return JSON.stringify(s);
}

function NewWakeupModal({ domain_uuid, onClose }: { domain_uuid: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'one_time' | 'recurring'>('one_time');
  const [oneTime, setOneTime] = useState(() => new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 16));
  const [days, setDays] = useState<string[]>(['mon', 'tue', 'wed', 'thu', 'fri']);
  const [time, setTime] = useState('07:00');
  const [extension_uuid, setExtensionUuid] = useState('');
  // Room picker — extensions in this department (a "room" is just an extension).
  const { data: allUsers = [] } = useQuery({
    queryKey: ['users-list-wakeup'],
    queryFn: () => api.users.list(),
  });
  const rooms = (allUsers as any[]).filter(u => u.domain_uuid === domain_uuid);
  const [type, setType] = useState<'tts' | 'recorded' | 'silent'>('tts');
  const [prompt, setPrompt] = useState('Good morning, this is your wake-up call.');

  const create = useMutation({
    mutationFn: () => api.wakeupCalls.create({
      domain_uuid,
      extension_uuid,
      schedule: mode === 'one_time' ? { one_time: new Date(oneTime).toISOString() } : { recurring: { days, time } },
      type,
      prompt: type === 'silent' ? null : prompt,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wakeup-calls', domain_uuid] });
      onClose();
    },
  });

  function toggleDay(d: string) {
    const lower = d.toLowerCase();
    setDays(days.includes(lower) ? days.filter(x => x !== lower) : [...days, lower]);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="card max-w-lg w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center gap-2"><BellRing size={18} /> Schedule wake-up</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={16} /></button>
        </div>

        <Field label="Room / Extension">
          <select className="input" value={extension_uuid} onChange={e => setExtensionUuid(e.target.value)}>
            <option value="" disabled>Select an extension…</option>
            {rooms.map((u: any) => (
              <option key={u.extension_uuid} value={u.extension_uuid}>
                {u.extension}{(u.directory_first_name || u.directory_last_name) ? ` — ${[u.directory_first_name, u.directory_last_name].filter(Boolean).join(' ')}` : ''}
              </option>
            ))}
          </select>
        </Field>

        <div className="flex gap-2">
          <button className={`btn ${mode === 'one_time' ? 'btn-primary' : ''}`} onClick={() => setMode('one_time')}>One-time</button>
          <button className={`btn ${mode === 'recurring' ? 'btn-primary' : ''}`} onClick={() => setMode('recurring')}>Recurring</button>
        </div>

        {mode === 'one_time' ? (
          <Field label="When">
            <input className="input" type="datetime-local" value={oneTime} onChange={e => setOneTime(e.target.value)} />
          </Field>
        ) : (
          <>
            <Field label="Days of week">
              <div className="flex gap-1 flex-wrap">
                {DAYS.map(d => (
                  <button key={d} onClick={() => toggleDay(d)}
                          className={`btn-ghost px-3 ${days.includes(d.toLowerCase()) ? 'bg-primary text-white' : ''}`}>{d}</button>
                ))}
              </div>
            </Field>
            <Field label="Time"><input className="input" type="time" value={time} onChange={e => setTime(e.target.value)} /></Field>
          </>
        )}

        <Field label="Type">
          <select className="input" value={type} onChange={e => setType(e.target.value as any)}>
            <option value="tts">Text-to-speech</option>
            <option value="recorded">Recorded file (path)</option>
            <option value="silent">Silent (just ring)</option>
          </select>
        </Field>
        {type !== 'silent' && (
          <Field label={type === 'tts' ? 'Message to speak' : 'Recorded file path'}>
            <input className="input" value={prompt} onChange={e => setPrompt(e.target.value)} />
          </Field>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!extension_uuid || create.isPending} onClick={() => create.mutate()}>
            <Clock size={14} className="inline mr-1" /> {create.isPending ? 'Saving…' : 'Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-1"><label className="text-sm font-medium">{label}</label>{children}</div>;
}
